import { cn } from "@comandero/shared";
import { AppSidebar } from "@/components/app-sidebar";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { useNavDrawerSwipe } from "@/features/layout/hooks/use-nav-drawer-swipe";

/**
 * Panel transform. Open and at rest it yields exactly `translate3d(0, 0, 0)`:
 * the drag only shows up in the style while there *is* a drag, so the resting
 * state stays a single comparable value.
 */
function panelTransform(isOpen: boolean, dragPx: number): string {
  if (!isOpen) {
    return "translate3d(-100%, 0, 0)";
  }
  return dragPx === 0 ? "translate3d(0, 0, 0)" : `translate3d(${dragPx}px, 0, 0)`;
}

interface AppLayoutSidebarProps {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
}

export function AppLayoutSidebar(props: AppLayoutSidebarProps) {
  // Browser "back" closes the mobile drawer instead of navigating (PWA pattern).
  useBackDismiss(
    () => props.isOpen(),
    () => props.setOpen(false),
  );

  let panel: HTMLElement | undefined;

  // Dragging left closes; dragging right does nothing (see hook).
  // Gesture listens on BOX, not panel, so it also works
  // when dragging from the dimmed backdrop.
  const swipe = useNavDrawerSwipe({
    isOpen: () => props.isOpen(),
    close: () => {
      props.setOpen(false);
    },
    panel: () => panel,
  });

  return (
    <>
      {/* ── Mobile Sidebar Drawer ───────────────────────────────── */}
      <div
        ref={(el) => {
          swipe.attach(el);
        }}
        data-testid="mobile-nav-drawer"
        class={cn(
          // `touch-pan-y`: browser retains vertical scroll and yields
          // horizontal axis. Without this it competes and drag cuts mid-swipe
          // on a real mobile device.
          "fixed inset-0 z-[100] lg:hidden overflow-hidden touch-pan-y",
          props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        {/* Backdrop */}
        <button
          type="button"
          data-testid="mobile-nav-backdrop"
          onClick={() => {
            props.setOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              props.setOpen(false);
            }
          }}
          class={cn(
            "absolute inset-0 bg-black/40 backdrop-blur-[2px] border-none p-0 w-full h-full cursor-default ease-swift",
            // While finger leads, backdrop follows finger: transition
            // is disabled and dragging sets opacity.
            swipe.isDragging() ? "transition-none" : "transition-opacity duration-300",
            props.isOpen() ? "opacity-100" : "opacity-0",
          )}
          style={{ opacity: swipe.isDragging() ? String(swipe.backdropOpacity()) : undefined }}
          // oxlint-disable-next-line lingui/no-unlocalized-strings -- i18n: aria-label deuda
          aria-label="Close sidebar"
        />
        {/* Drawer */}
        <aside
          ref={panel}
          data-testid="mobile-nav-aside"
          class={cn(
            "absolute left-0 top-0 bottom-0 flex w-[280px] flex-col border-r border-border/40 bg-card/85 backdrop-blur-md shadow-2xl ease-swift will-change-transform",
            "pl-[var(--safe-area-inset-left)] pb-[var(--safe-area-inset-bottom)]",
            // With transition on, panel would arrive 300ms late to
            // where finger already is.
            swipe.isDragging() ? "transition-none" : "transition-transform duration-300",
          )}
          style={{
            transform: panelTransform(props.isOpen(), swipe.dragPx()),
          }}
        >
          <AppSidebar
            onCloseSidebar={() => {
              props.setOpen(false);
            }}
          />
        </aside>
      </div>

      {/* ── Desktop Sidebar ───────────────────────────────────── */}
      {/* `app-shell-sidebar`: ancla del `MutationObserver` de la sonda de I1
          (P23 / CC-60). Marca el shell para poder medir el HUECO en que se
          desconecta del DOM; un test de identidad no ve la desconexión porque
          los nodos que vuelven son los mismos. */}
      <aside
        data-testid="app-shell-sidebar"
        class="hidden lg:flex w-64 shrink-0 flex-col border-r border-border/40 bg-card/10 backdrop-blur-md"
      >
        <AppSidebar />
      </aside>
    </>
  );
}
